'use strict'

import React, { useState, useCallback } from 'react'

import { ChrofyShowErrors } from '../../../common/Form'
import { ChrofyCustomSelect } from '../../../common/ChrofyCustomSelect'
import { getMessage } from '../../../../common/utils/MessageUtils'
import { astCol, astLiteral } from '../extra/conditionExpressionAst'
import {
  CONDITION_MAPPING_DATE_FORMAT_ERROR,
  CONDITION_MAPPING_DATE_PLACEHOLDER,
  isValidConditionMappingDateValue
} from '../extra/validation'
const OPERATOR_OPTIONS = [
  { value: '==', label: '=' },
  { value: '!=', label: '≠' },
  { value: '>', label: '>' },
  { value: '>=', label: '≥' },
  { value: '<', label: '<' },
  { value: '<=', label: '≤' }
]
const COMPARE_TYPE_OPTIONS = [
  { value: 'fixed', label: '固定値' },
  { value: 'item', label: '項目' },
  { value: 'date', label: '日付' },
  { value: 'empty', label: '(空)' }
]
const RESULT_TYPE_OPTIONS = [
  { value: 'fixed', label: '固定値' },
  { value: 'item', label: '項目' },
  { value: 'date', label: '日付' },
  { value: 'empty', label: '(空)' }
]
const ERRORS = {
  E10206_10_001: getMessage('E011017', '条件セット'),
  E10206_10_002: getMessage('E011017', '条件式'),
  E10206_10_003: getMessage('E010013', '同一条件'),
  E10206_10_004: getMessage('E010013', '同じ条件のセット'),
  E10206_10_005: getMessage('E010017', 'ファイル種別'),
  E10206_10_006: getMessage('E010017', '項目名'),
  E10206_10_007: getMessage('E010001', '比較値'),
  COMPARE_DATE_FORMAT: getMessage('E010002', '比較値（日付）'),
  E10206_10_008: getMessage('E010017', '演算子'),
  E10206_10_009: getMessage('E010001', '結果値'),
  E10206_10_010: getMessage('E010002', '入力内容'),
  COMPARE_ITEM_REQUIRED: getMessage('E010017', '比較項目'),
  RESULT_VALUE_REQUIRED: getMessage('E010001', '結果値'),
  RESULT_ITEM_REQUIRED: getMessage('E010017', '結果項目'),
  RESULT_DATE_FORMAT: getMessage('E010002', '結果値（日付）'),
  ELSE_VALUE_REQUIRED: getMessage('E010001', 'Elseの結果値'),
  ELSE_ITEM_REQUIRED: getMessage('E010017', 'Elseの結果項目'),
  ELSE_DATE_FORMAT: CONDITION_MAPPING_DATE_FORMAT_ERROR
}

/**
 * 条件行の結果値（日付）を AST 用 Literal にする
 *
 * 入力形式（`YYYY/MM/DD` / `YYYY-MM-DD`）を正規化せずそのまま保存する。 CHROFY 標準形式でない値は検証（プレビュー）でエラーとして検出させるため。
 *
 * @param {string} raw
 * @returns {object}
 */
function conditionResultDateLiteralForAst(raw) {
  return astLiteral(String(raw ?? '').trim())
}

/**
 * Else 日付を CHROFY 標準形式へ正規化して AST 用 Literal を返す
 *
 * @param {string} raw
 * @returns {object}
 */
function elseDateLiteralForAst(raw) {
  return astLiteral(String(raw ?? '').trim())
}

/**
 * @typedef {{
 *   fileType: string
 *   column: string
 *   operator: string
 *   compareType: string
 *   compareValue: string
 *   compareFileType?: string
 *   compareColumn?: string
 * }} CondExpr
 */
/**
 * @typedef {{
 *   id: string
 *   joinCondition: string
 *   expressions: CondExpr[]
 *   resultType: string
 *   resultValue: string
 *   resultFileType?: string
 *   resultColumn?: string
 * }} ConditionSetUI
 */
/**
 * @typedef {{
 *   conditionSets: ConditionSetUI[]
 *   elseResultType: string
 *   elseResultValue: string
 *   elseResultFileType?: string
 *   elseResultColumn?: string
 * }} FormDataMulti
 */

/**
 * AST を保存用 JSON 文字列に変換する
 *
 * @param {unknown} node
 * @returns {string}
 */
function astToStoredString(node) {
  return JSON.stringify(node)
}

/**
 * 保存済みの条件式/値を AST に復元する
 *
 * @param {unknown} stored
 * @returns {any}
 */
function storedStringToAst(stored) {
  if (stored?.type) return stored
  if (stored === 'true') return { type: 'Literal', value: true }
  if (typeof stored !== 'string' || !stored.trim()) return null
  try {
    return JSON.parse(stored)
  } catch {
    return null
  }
}

/**
 * CallExpression の引数ノードからスカラー文字列を取り出す（jsep Literal・レガシー・生文字列）
 *
 * @param {unknown} arg
 * @returns {string}
 */
function argToScalar(arg) {
  if (arg == null) return ''
  if (typeof arg === 'string') return arg
  if (typeof arg === 'object') {
    if (Object.prototype.hasOwnProperty.call(arg, 'value') || Object.prototype.hasOwnProperty.call(arg, 'Value')) {
      return String(
        /** @type {{ value?: unknown; Value?: unknown }} */ (arg).value ??
          /** @type {{ value?: unknown; Value?: unknown }} */ (arg).Value ??
          ''
      )
    }
    if (/** @type {{ type?: string }} */ (arg).type === 'Literal') {
      return String(/** @type {{ value?: unknown }} */ (arg).value ?? '')
    }
  }
  return String(arg)
}

/**
 * 列参照 AST から fileType / column を取り出す
 *
 * @param {any} node
 * @returns {{ fileType: string; column: string } | null}
 */
function parseColumnNode(node) {
  if (!node || typeof node !== 'object') return null
  const args = node.Arguments ?? node.arguments
  if (!Array.isArray(args) || args.length < 2) return null

  const nodeType = node.Type ?? node.type
  const isCall = nodeType === 'CallExpression'
  const isLegacyColumn = nodeType === 'Column'

  if (isCall) {
    const callee = node.callee ?? node.Callee
    const nameFromCallee = callee && typeof callee === 'object' ? (callee.name ?? callee.Name) : undefined
    const legacyName = node.name ?? node.Name
    if ((nameFromCallee ?? legacyName) !== 'col') return null
  } else if (isLegacyColumn) {
    // レガシー { Type: 'Column', Arguments: [...] }
  } else {
    const legacyName = node.name ?? node.Name
    if (legacyName !== 'col') return null
  }

  return {
    fileType: argToScalar(args[0]),
    column: argToScalar(args[1])
  }
}

/**
 * AST から UI の条件式に変換する
 *
 * @param {any} exprAst
 * @returns {CondExpr | null}
 */
function expressionAstToUi(exprAst) {
  if (!exprAst || typeof exprAst !== 'object') return null
  const left = exprAst.Left ?? exprAst.left ?? {}
  const right = exprAst.Right ?? exprAst.right ?? {}
  const op = exprAst.Operator ?? exprAst.operator ?? '=='
  const leftCol = parseColumnNode(left)
  const rightCol = parseColumnNode(right)
  const rightVal = right.Value ?? right.value

  let compareType = 'fixed'
  if (rightVal === '') {
    compareType = 'empty'
  } else if (rightVal !== undefined && rightVal !== null) {
    compareType = 'fixed'
  } else if (rightCol) {
    compareType = 'item'
  }

  return {
    fileType: leftCol?.fileType ?? '',
    column: leftCol?.column ?? '',
    operator: op,
    compareType,
    compareValue: rightVal !== undefined && rightVal !== null ? String(rightVal) : '',
    compareFileType: rightCol?.fileType ?? undefined,
    compareColumn: rightCol?.column ?? undefined
  }
}

/**
 * 値 AST から UI の結果値に変換する
 *
 * @param {any} valueAst
 * @returns {{ resultType: string; resultValue: string; resultFileType?: string; resultColumn?: string }}
 */
function valueAstToUi(valueAst) {
  if (!valueAst || typeof valueAst !== 'object') {
    return { resultType: 'fixed', resultValue: '' }
  }

  const type = valueAst.Type ?? valueAst.type ?? 'Literal'
  const value = valueAst.Value ?? valueAst.value
  const columnRef = parseColumnNode(valueAst)

  let resultType = 'fixed'
  if (type === 'Column' || columnRef) {
    resultType = 'item'
  } else if (type === 'Date') {
    resultType = 'date'
  } else if (value === '') {
    resultType = 'empty'
  }

  return {
    resultType,
    resultValue: String(value ?? ''),
    resultFileType: columnRef?.fileType ?? undefined,
    resultColumn: columnRef?.column ?? undefined
  }
}

/**
 * UI の条件式を AST に変換する
 *
 * @param {CondExpr} ex
 * @returns {object}
 */
function expressionUiToAst(ex) {
  const left = astCol(ex.fileType, ex.column)
  let right
  if (ex.compareType === 'item' && ex.compareFileType && ex.compareColumn) {
    right = astCol(ex.compareFileType, ex.compareColumn)
  } else if (ex.compareType === 'empty') {
    right = astLiteral('')
  } else {
    right = astLiteral(String(ex.compareValue ?? '').trim())
  }
  return {
    type: 'BinaryExpression',
    operator: ex.operator,
    left,
    right
  }
}

/**
 * UI の結果値を AST に変換する
 *
 * @param {{ resultType: string; resultValue: string; resultFileType?: string; resultColumn?: string }} valueState
 * @param {{ normalizeHyphenDate?: boolean }} [options]
 * @returns {object}
 */
function valueUiToAst(valueState, options = {}) {
  if (valueState.resultType === 'item' && valueState.resultFileType && valueState.resultColumn) {
    return astCol(valueState.resultFileType, valueState.resultColumn)
  }
  if (valueState.resultType === 'empty') {
    return astLiteral('')
  }
  const v = String(valueState.resultValue ?? '').trim()
  if (valueState.resultType === 'date') {
    if (options.normalizeHyphenDate) {
      return elseDateLiteralForAst(v)
    }
    return conditionResultDateLiteralForAst(v)
  }
  return astLiteral(v)
}

/**
 * FormState を UI 既定値込みで正規化する
 *
 * @param {unknown} formState
 * @returns {FormDataMulti | null}
 */
function normalizeStoredFormState(formState) {
  if (!formState || typeof formState !== 'object') return null
  const normalized = /** @type {Record<string, unknown>} */ (formState)
  const conditionSets = Array.isArray(normalized.conditionSets) ? normalized.conditionSets : []
  return {
    conditionSets:
      conditionSets.length > 0
        ? /** @type {ConditionSetUI[]} */ (conditionSets)
        : [
            {
              id: `set-0-${Date.now()}`,
              joinCondition: 'and',
              expressions: [{ fileType: '', column: '', operator: '==', compareType: 'fixed', compareValue: '' }],
              resultType: 'fixed',
              resultValue: ''
            }
          ],
    elseResultType: String(normalized.elseResultType ?? 'fixed'),
    elseResultValue: String(normalized.elseResultValue ?? ''),
    elseResultFileType: String(normalized.elseResultFileType ?? ''),
    elseResultColumn: String(normalized.elseResultColumn ?? '')
  }
}

/**
 * Nested multiCondition から form_state を抽出する
 *
 * @param {unknown[]} list
 * @returns {unknown}
 */
function extractNestedFormState(list) {
  for (const item of list) {
    const conditionSet = item?.conditionSet ?? item?.ConditionSet
    if (!Array.isArray(conditionSet)) continue
    for (const set of conditionSet) {
      const exprList = set?.conditionalExpressionList ?? set?.ConditionalExpressionList
      if (!Array.isArray(exprList)) continue
      for (const expr of exprList) {
        const formState = expr?.formState ?? expr?.form_state
        if (formState != null) return formState
      }
    }
  }
  return null
}

/**
 * MappingInfo multi_condition → formData (DB→UI)
 *
 * @param {{
 *   mappingType?: string
 *   formState?: FormDataMulti
 *   MultiCondition?: unknown[]
 *   multiCondition?: unknown[]
 * } | null} initial
 * @param {import('../extra/types').FileTypeInfo[]} fileTypes
 * @returns {FormDataMulti}
 */
export function mappingInfoToFormData(initial, fileTypes) {
  const multi = initial?.MultiCondition ?? initial?.multiCondition
  const normalizedFormState = normalizeStoredFormState(
    initial?.formState ?? initial?.form_state ?? extractNestedFormState(Array.isArray(multi) ? multi : [])
  )
  if (normalizedFormState) return normalizedFormState
  return multiConditionDbToUi(multi)
}

/**
 * FormData → MappingInfo multi_condition (UI→DB)
 *
 * @param {FormDataMulti} formData
 * @returns {{
 *   mappingType: 'multi_condition'
 *   multiCondition: {
 *     conditionSet: {
 *       joinCondition: string
 *       conditionalExpressionList: { conditionalExpression: string }[]
 *     }[]
 *     value: string
 *   }[]
 *   formState: FormDataMulti
 * }}
 */
export function formDataToMappingInfo(formData) {
  return {
    mappingType: 'multi_condition',
    multiCondition: multiConditionUiToDb(formData),
    formState: trimFormStateForStore(formData)
  }
}

/**
 * FormState 保存用に自由入力の文字列（比較値・結果値・Elseの結果値）をトリムする。 AST（multiCondition）側のトリムと表示用 formState を一致させ、再編集時に空白付きで復元されないようにする
 *
 * @param {FormDataMulti} formData
 * @returns {FormDataMulti}
 */
function trimFormStateForStore(formData) {
  return {
    ...formData,
    conditionSets: (formData.conditionSets ?? []).map((set) => ({
      ...set,
      expressions: (set.expressions ?? []).map((ex) => ({
        ...ex,
        compareValue: String(ex.compareValue ?? '').trim()
      })),
      resultValue: String(set.resultValue ?? '').trim()
    })),
    elseResultValue: String(formData.elseResultValue ?? '').trim()
  }
}

/**
 * Validate multi_condition form
 *
 * @param {FormDataMulti} formData
 * @returns {Record<string, string[]> | null}
 */
export function validateMultiConditionForm(formData) {
  /** @type {Record<string, string[]>} */
  const nextErrors = {}
  // 同一キーへの重複メッセージは抑止して、該当セット直下にまとめて表示する。
  const add = (key, msg) => {
    if (!nextErrors[key]) nextErrors[key] = []
    if (!nextErrors[key].includes(msg)) nextErrors[key].push(msg)
  }

  const conditionSets = formData?.conditionSets ?? []
  if (conditionSets.length === 0) {
    add('general', ERRORS.E10206_10_001)
  } else {
    for (const set of conditionSets) {
      const setKey = `set-${set.id}`
      if (!set.expressions || set.expressions.length === 0) {
        add(setKey, ERRORS.E10206_10_002)
        continue
      }
      const exprKeys = set.expressions.map(exprKey)
      if (new Set(exprKeys).size !== exprKeys.length) add(setKey, ERRORS.E10206_10_003)

      // メッセージは setKey にまとめて表示。赤枠用に各フィールド単位キーにも積む（単一条件と同様）
      set.expressions.forEach((ex, j) => {
        const addField = (field, msg) => {
          add(setKey, msg)
          add(`expr-${set.id}-${j}-${field}`, msg)
        }
        if (!ex.fileType?.trim()) addField('fileType', ERRORS.E10206_10_005)
        if (!ex.column?.trim()) addField('column', ERRORS.E10206_10_006)
        // 比較値: 演算子は既定 '==' のため未選択は発生しない（チェック対象外）。
        if (ex.compareType === 'item') {
          if (!ex.compareFileType?.trim() || !ex.compareColumn?.trim())
            addField('compareValue', ERRORS.COMPARE_ITEM_REQUIRED)
        } else if (ex.compareType !== 'empty') {
          if (!String(ex.compareValue ?? '').trim()) addField('compareValue', ERRORS.E10206_10_007)
          else if (ex.compareType === 'date' && !isValidConditionMappingDateValue(ex.compareValue))
            addField('compareValue', ERRORS.COMPARE_DATE_FORMAT)
        }
      })

      // 結果値（すべての条件を満たす場合）。メッセージは setKey、赤枠は result-${set.id} へ
      const addResult = (msg) => {
        add(setKey, msg)
        add(`result-${set.id}`, msg)
      }
      if (set.resultType === 'item') {
        if (!set.resultFileType?.trim() || !set.resultColumn?.trim()) addResult(ERRORS.RESULT_ITEM_REQUIRED)
      } else if (set.resultType !== 'empty') {
        if (!String(set.resultValue ?? '').trim()) addResult(ERRORS.RESULT_VALUE_REQUIRED)
        else if (set.resultType === 'date' && !isValidConditionMappingDateValue(set.resultValue))
          addResult(ERRORS.RESULT_DATE_FORMAT)
      }
    }
    const setKeys = conditionSets.map(setContentKey)
    if (new Set(setKeys).size < setKeys.length) add('general', ERRORS.E10206_10_004)
  }

  // Else（すべての条件セットを満たさない場合）の結果値
  const elseType = formData?.elseResultType ?? 'fixed'
  // メッセージは general（フォーム下部）、赤枠は else キーへ
  const addElse = (msg) => {
    add('general', msg)
    add('else', msg)
  }
  if (elseType === 'item') {
    if (!formData?.elseResultFileType?.trim() || !formData?.elseResultColumn?.trim()) {
      addElse(ERRORS.ELSE_ITEM_REQUIRED)
    }
  } else if (elseType !== 'empty') {
    if (!String(formData?.elseResultValue ?? '').trim()) addElse(ERRORS.ELSE_VALUE_REQUIRED)
    else if (elseType === 'date' && !isValidConditionMappingDateValue(formData?.elseResultValue))
      addElse(ERRORS.ELSE_DATE_FORMAT)
  }

  return Object.keys(nextErrors).length > 0 ? nextErrors : null
}

/**
 * DB MultiCondition を UI formData に変換（SR-154, SR-156, SR-178）
 *
 * @param {unknown} multiCondition
 * @returns {FormDataMulti}
 */
function multiConditionDbToUi(multiCondition) {
  const list = Array.isArray(multiCondition) ? multiCondition : []
  const conditionSets = []
  let elseResultType = 'fixed'
  let elseResultValue = ''
  let elseResultFileType = ''
  let elseResultColumn = ''

  for (let i = 0; i < list.length; i++) {
    const item = list[i]
    if (!item || typeof item !== 'object') continue

    const raw = item
    let rawConditionSets = []
    if (Array.isArray(raw.conditionSet ?? raw.ConditionSet)) {
      rawConditionSets = raw.conditionSet ?? raw.ConditionSet
    } else if (Array.isArray(raw.ConditionalExpressionList ?? raw.conditionalExpressionList)) {
      rawConditionSets = [
        {
          joinCondition: raw.JoinCondition ?? raw.joinCondition ?? 'and',
          conditionalExpressionList: raw.ConditionalExpressionList ?? raw.conditionalExpressionList
        }
      ]
    }

    const valueAst = storedStringToAst(raw.value ?? raw.Value)

    if (rawConditionSets.length === 0) {
      const elseUi = valueAstToUi(valueAst)
      elseResultType = elseUi.resultType
      elseResultValue = elseUi.resultValue
      elseResultFileType = elseUi.resultFileType ?? ''
      elseResultColumn = elseUi.resultColumn ?? ''
      continue
    }

    rawConditionSets.forEach((set, setIndex) => {
      if (!set || typeof set !== 'object') return
      const rawSet = set
      const joinCondition = rawSet.joinCondition ?? rawSet.join_condition ?? rawSet.JoinCondition ?? 'and'
      const exprList =
        rawSet.conditionalExpressionList ?? rawSet.conditional_expression_list ?? rawSet.ConditionalExpressionList ?? []
      if (!Array.isArray(exprList) || exprList.length === 0) return

      const serializedExpressions = exprList
        .map((expr) =>
          storedStringToAst(
            expr?.conditionalExpression ?? expr?.conditional_expression ?? expr?.ConditionalExpression ?? expr
          )
        )
        .filter(Boolean)

      if (
        serializedExpressions.length === 1 &&
        serializedExpressions[0]?.type === 'Literal' &&
        serializedExpressions[0]?.value === true
      ) {
        const elseUi = valueAstToUi(valueAst)
        elseResultType = elseUi.resultType
        elseResultValue = elseUi.resultValue
        elseResultFileType = elseUi.resultFileType ?? ''
        elseResultColumn = elseUi.resultColumn ?? ''
        return
      }

      const expressions = serializedExpressions.map(expressionAstToUi).filter(Boolean)
      if (expressions.length === 0) return
      const resultUi = valueAstToUi(valueAst)
      conditionSets.push({
        id: `set-${i}-${setIndex}-${Date.now()}`,
        joinCondition,
        expressions,
        resultType: resultUi.resultType,
        resultValue: resultUi.resultValue,
        resultFileType: resultUi.resultFileType,
        resultColumn: resultUi.resultColumn
      })
    })
  }

  if (conditionSets.length === 0) {
    conditionSets.push({
      id: `set-0-${Date.now()}`,
      joinCondition: 'and',
      expressions: [{ fileType: '', column: '', operator: '==', compareType: 'fixed', compareValue: '' }],
      resultType: 'fixed',
      resultValue: ''
    })
  }

  return { conditionSets, elseResultType, elseResultValue, elseResultFileType, elseResultColumn }
}

/**
 * UI formData を DB MultiCondition に変換（SR-154, SR-156, SR-157）
 *
 * @param {FormDataMulti} formData
 * @returns {{
 *   conditionSet: {
 *     joinCondition: string
 *     conditionalExpressionList: { conditionalExpression: string }[]
 *   }[]
 *   value: string
 * }[]}
 */
function multiConditionUiToDb(formData) {
  const out = []
  const { conditionSets, elseResultType, elseResultValue } = formData

  for (const set of conditionSets) {
    if (set.expressions.length === 0) continue
    const conditionalExpressionList = set.expressions.map((ex) => ({
      conditionalExpression: astToStoredString(expressionUiToAst(ex))
    }))
    out.push({
      conditionSet: [
        {
          joinCondition: set.joinCondition,
          conditionalExpressionList
        }
      ],
      value: astToStoredString(
        valueUiToAst({
          resultType: set.resultType,
          resultValue: set.resultValue,
          resultFileType: set.resultFileType,
          resultColumn: set.resultColumn
        })
      )
    })
  }

  const elseFt = formData.elseResultFileType ?? ''
  const elseCol = formData.elseResultColumn ?? ''
  out.push({
    conditionSet: [
      {
        joinCondition: 'or',
        conditionalExpressionList: [{ conditionalExpression: JSON.stringify({ type: 'Literal', value: true }) }]
      }
    ],
    value: astToStoredString(
      valueUiToAst(
        {
          resultType: elseResultType,
          resultValue: elseResultValue,
          resultFileType: elseFt,
          resultColumn: elseCol
        },
        { normalizeHyphenDate: true }
      )
    )
  })
  return out
}

/**
 * 同一条件のキーを生成（重複チェック用）
 *
 * @param {CondExpr} ex
 * @returns {string}
 */
function exprKey(ex) {
  return [
    ex.fileType,
    ex.column,
    ex.operator,
    ex.compareType,
    ex.compareValue,
    ex.compareFileType ?? '',
    ex.compareColumn ?? ''
  ].join('|')
}

/**
 * セットの内容キー（順序無視で重複セット検出）
 *
 * @param {ConditionSetUI} set
 * @returns {string}
 */
function setContentKey(set) {
  const keys = set.expressions.map(exprKey).sort()
  const resultPart = [set.resultType, set.resultValue, set.resultFileType ?? '', set.resultColumn ?? ''].join('|')
  return keys.join(';') + '::' + resultPart
}

/** 複数項目条件モーダル本体（SR-173〜SR-178, SR-183〜SR-185） */
export const MultiConditionForm = (props) => {
  const { fileTypes = [], formData, errors, onChange } = props

  const updateForm = useCallback(
    (updater) => {
      const next = typeof updater === 'function' ? updater(formData) : updater
      onChange(next, () => ({}))
    },
    [formData, onChange]
  )

  const addSet = useCallback(() => {
    updateForm((fd) => ({
      ...fd,
      conditionSets: [
        ...(fd.conditionSets ?? []),
        {
          id: `set-${Date.now()}`,
          joinCondition: 'and',
          expressions: [{ fileType: '', column: '', operator: '==', compareType: 'fixed', compareValue: '' }],
          resultType: 'fixed',
          resultValue: ''
        }
      ]
    }))
  }, [updateForm])

  const removeSet = useCallback(
    (setId) => {
      updateForm((fd) => ({
        ...fd,
        conditionSets: (fd.conditionSets ?? []).filter((s) => s.id !== setId)
      }))
    },
    [updateForm]
  )

  const updateSet = useCallback(
    (setId, updater) => {
      updateForm((fd) => ({
        ...fd,
        conditionSets: (fd.conditionSets ?? []).map((s) =>
          s.id === setId ? (typeof updater === 'function' ? updater(s) : updater) : s
        )
      }))
    },
    [updateForm]
  )

  const addExpression = useCallback(
    (setId) => {
      updateSet(setId, (s) => ({
        ...s,
        expressions: [
          ...s.expressions,
          { fileType: '', column: '', operator: '==', compareType: 'fixed', compareValue: '' }
        ]
      }))
    },
    [updateSet]
  )

  const removeExpression = useCallback(
    (setId, exprIndex) => {
      updateSet(setId, (s) => ({
        ...s,
        expressions: s.expressions.filter((_, i) => i !== exprIndex)
      }))
    },
    [updateSet]
  )

  const updateExpression = useCallback(
    (setId, exprIndex, field, value) => {
      updateSet(setId, (s) => {
        const expressions = [...s.expressions]
        if (expressions[exprIndex]) {
          const next = { ...expressions[exprIndex], [field]: value }
          // ファイル種別変更時は依存する項目名をリセット（新種別に属さない項目の持ち越し防止）
          if (field === 'fileType') next.column = ''
          if (field === 'compareFileType') next.compareColumn = ''
          expressions[exprIndex] = next
        }
        return { ...s, expressions }
      })
    },
    [updateSet]
  )

  const conditionSets = formData?.conditionSets ?? []

  return (
    <div className="text-h7">
      <div>
        {conditionSets.map((set, setIndex) => {
          const isColumnDisabled = (ft) => !ft
          const fileOpts = (fileTypes || []).map((f) => ({ label: f.mappingFileType, value: f.mappingFileType }))
          return (
            <div key={set.id} className="mb-4 rounded-lg border-2 border-blue1 p-4" style={{ background: '#fafbfc' }}>
              <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                <span className="text-h7 font-bold blue1--text">条件セット{setIndex + 1}</span>
                <div className="flex items-center gap-3">
                  <div
                    className="flex items-center gap-2 rounded border border-blue1 bg-white px-2.5 py-1"
                    style={{ borderColor: 'rgba(49, 80, 165, 0.4)' }}
                  >
                    <span className="text-h7 blue1--text">条件結合:</span>
                    <label className="flex cursor-pointer items-center gap-1 text-h7 blue1--text">
                      <input
                        type="radio"
                        name={`set_join_${set.id}`}
                        value="and"
                        checked={set.joinCondition === 'and'}
                        onChange={() => updateSet(set.id, (s) => ({ ...s, joinCondition: 'and' }))}
                        className="m-0"
                        style={{ accentColor: '#3150A5' }}
                      />
                      AND
                    </label>
                    <label className="flex cursor-pointer items-center gap-1 text-h7 blue1--text">
                      <input
                        type="radio"
                        name={`set_join_${set.id}`}
                        value="or"
                        checked={set.joinCondition === 'or'}
                        onChange={() => updateSet(set.id, (s) => ({ ...s, joinCondition: 'or' }))}
                        className="m-0"
                        style={{ accentColor: '#3150A5' }}
                      />
                      OR
                    </label>
                  </div>
                  {conditionSets.length > 1 && (
                    <button
                      type="button"
                      className="rounded border border-gray-300 bg-white px-3 py-1.5 text-sm text-gray-700 transition-colors hover:bg-gray-100"
                      onClick={() => removeSet(set.id)}
                    >
                      セットを削除
                    </button>
                  )}
                </div>
              </div>
              <div className="mb-3 rounded border border-gray-200 bg-white p-3">
                {set.expressions.map((ex, exprIndex) => {
                  const selFile = fileTypes.find((f) => f.mappingFileType === ex.fileType)
                  const cols = (selFile?.headers ?? []).map((h) => ({ label: h, value: h }))
                  return (
                    <div
                      key={exprIndex}
                      className="mb-2 flex flex-wrap items-center gap-3 rounded bg-gray-50 p-2 last:mb-0"
                    >
                      <span className="min-w-[20px] text-h7 font-medium text-gray-600">{exprIndex + 1}.</span>
                      <div className="min-w-[100px] flex-1">
                        <ChrofyCustomSelect
                          id={`multi-ex-file-${set.id}-${exprIndex}`}
                          name={`multi-ex-file-${set.id}-${exprIndex}`}
                          isError={Boolean(errors?.[`expr-${set.id}-${exprIndex}-fileType`]?.length)}
                          value={ex.fileType}
                          onChange={(e) => updateExpression(set.id, exprIndex, 'fileType', e.target.value)}
                          className="min-w-0 w-full"
                          options={fileOpts}
                          disabled={false}
                          hasNoSelect={true}
                          searchable
                        />
                      </div>
                      <div className="min-w-[100px] flex-1">
                        <ChrofyCustomSelect
                          id={`multi-ex-column-${set.id}-${exprIndex}`}
                          name={`multi-ex-column-${set.id}-${exprIndex}`}
                          isError={Boolean(errors?.[`expr-${set.id}-${exprIndex}-column`]?.length)}
                          value={ex.column}
                          onChange={(e) => updateExpression(set.id, exprIndex, 'column', e.target.value)}
                          className={isColumnDisabled(ex.fileType) ? 'min-w-0 w-full opacity-60' : 'min-w-0 w-full'}
                          options={cols}
                          disabled={isColumnDisabled(ex.fileType)}
                          hasNoSelect={true}
                          searchable
                        />
                      </div>
                      <div className="min-w-[60px] flex-1">
                        <ChrofyCustomSelect
                          id={`multi-ex-operator-${set.id}-${exprIndex}`}
                          name={`multi-ex-operator-${set.id}-${exprIndex}`}
                          value={ex.operator}
                          onChange={(e) => updateExpression(set.id, exprIndex, 'operator', e.target.value)}
                          className="min-w-0 w-full"
                          options={OPERATOR_OPTIONS}
                          disabled={false}
                          hasNoSelect={false}
                        />
                      </div>
                      <div className="min-w-[80px] flex-1">
                        <ChrofyCustomSelect
                          id={`multi-ex-compare-type-${set.id}-${exprIndex}`}
                          name={`multi-ex-compare-type-${set.id}-${exprIndex}`}
                          value={ex.compareType}
                          onChange={(e) => updateExpression(set.id, exprIndex, 'compareType', e.target.value)}
                          className="min-w-0 w-full"
                          options={COMPARE_TYPE_OPTIONS}
                          disabled={false}
                          hasNoSelect={false}
                        />
                      </div>
                      {ex.compareType === 'item' && (
                        <>
                          <div className="min-w-[100px] flex-1">
                            <ChrofyCustomSelect
                              id={`multi-ex-compare-file-${set.id}-${exprIndex}`}
                              name={`multi-ex-compare-file-${set.id}-${exprIndex}`}
                              isError={Boolean(errors?.[`expr-${set.id}-${exprIndex}-compareValue`]?.length)}
                              value={ex.compareFileType ?? ''}
                              onChange={(e) => updateExpression(set.id, exprIndex, 'compareFileType', e.target.value)}
                              className="min-w-0 w-full"
                              options={fileOpts}
                              disabled={false}
                              hasNoSelect={true}
                              searchable
                            />
                          </div>
                          <div className="min-w-[100px] flex-1">
                            <ChrofyCustomSelect
                              id={`multi-ex-compare-col-${set.id}-${exprIndex}`}
                              name={`multi-ex-compare-col-${set.id}-${exprIndex}`}
                              isError={Boolean(errors?.[`expr-${set.id}-${exprIndex}-compareValue`]?.length)}
                              value={ex.compareColumn ?? ''}
                              onChange={(e) => updateExpression(set.id, exprIndex, 'compareColumn', e.target.value)}
                              className="min-w-0 w-full"
                              options={(
                                fileTypes.find((f) => f.mappingFileType === (ex.compareFileType ?? ''))?.headers ?? []
                              ).map((h) => ({ label: h, value: h }))}
                              disabled={!ex.compareFileType}
                              hasNoSelect={true}
                              searchable
                            />
                          </div>
                        </>
                      )}
                      {ex.compareType !== 'item' && ex.compareType !== 'empty' && (
                        <div className="min-w-[100px] flex-1">
                          <input
                            type="text"
                            className="text-h7 h-11 w-full rounded-xl border bg-white pl-4 pr-3 outline-none transition-colors focus:border-[#297ad8]"
                            style={{
                              borderColor: errors?.[`expr-${set.id}-${exprIndex}-compareValue`]?.length
                                ? '#f4392c'
                                : 'rgba(116, 118, 166, 0.3)'
                            }}
                            value={ex.compareValue}
                            onChange={(e) => updateExpression(set.id, exprIndex, 'compareValue', e.target.value)}
                            placeholder={ex.compareType === 'date' ? CONDITION_MAPPING_DATE_PLACEHOLDER : '値'}
                          />
                        </div>
                      )}
                      {set.expressions.length > 1 ? (
                        <button
                          type="button"
                          className="rounded border border-gray-300 bg-white px-2 py-1 text-sm text-gray-600 transition-colors hover:bg-gray-100"
                          onClick={() => removeExpression(set.id, exprIndex)}
                        >
                          ×
                        </button>
                      ) : (
                        <span className="w-[30px]" />
                      )}
                    </div>
                  )
                })}
                <button
                  type="button"
                  className="mt-2 rounded bg-[#3150A5] px-3 py-1.5 text-sm font-medium text-white transition-opacity hover:opacity-90"
                  onClick={() => addExpression(set.id)}
                >
                  + 条件を追加
                </button>
              </div>
              <div className="flex flex-wrap items-center gap-3 rounded p-3" style={{ background: '#e8f4f8' }}>
                <span className="shrink-0 text-h7 font-medium blue1--text">すべての条件を満たす場合 →</span>
                <div className="basis-[128px] shrink min-w-[112px]">
                  <ChrofyCustomSelect
                    id={`multi-result-type-${set.id}`}
                    name={`multi-result-type-${set.id}`}
                    value={set.resultType}
                    onChange={(e) => updateSet(set.id, (s) => ({ ...s, resultType: e.target.value }))}
                    className="min-w-0 w-full"
                    options={RESULT_TYPE_OPTIONS}
                    disabled={false}
                    hasNoSelect={false}
                  />
                </div>
                {set.resultType === 'item' && (
                  <>
                    <div className="basis-[180px] shrink min-w-[140px]">
                      <ChrofyCustomSelect
                        id={`multi-result-file-${set.id}`}
                        name={`multi-result-file-${set.id}`}
                        isError={Boolean(errors?.[`result-${set.id}`]?.length)}
                        value={set.resultFileType ?? ''}
                        onChange={(e) =>
                          updateSet(set.id, (s) => ({ ...s, resultFileType: e.target.value, resultColumn: '' }))
                        }
                        className="min-w-0 w-full"
                        options={fileOpts}
                        disabled={false}
                        hasNoSelect={true}
                      />
                    </div>
                    <div className="basis-[180px] shrink min-w-[140px]">
                      <ChrofyCustomSelect
                        id={`multi-result-col-${set.id}`}
                        name={`multi-result-col-${set.id}`}
                        isError={Boolean(errors?.[`result-${set.id}`]?.length)}
                        value={set.resultColumn ?? ''}
                        onChange={(e) => updateSet(set.id, (s) => ({ ...s, resultColumn: e.target.value }))}
                        className="min-w-0 w-full"
                        options={(
                          fileTypes.find((f) => f.mappingFileType === (set.resultFileType ?? ''))?.headers ?? []
                        ).map((h) => ({ label: h, value: h }))}
                        disabled={!set.resultFileType}
                        hasNoSelect={true}
                      />
                    </div>
                  </>
                )}
                {set.resultType !== 'item' && set.resultType !== 'empty' && (
                  <div className="basis-[260px] shrink min-w-[180px]">
                    <input
                      type="text"
                      className="text-h7 h-11 w-full rounded-xl border bg-white pl-4 pr-3 outline-none transition-colors focus:border-[#297ad8]"
                      style={{
                        borderColor: errors?.[`result-${set.id}`]?.length ? '#f4392c' : 'rgba(116, 118, 166, 0.3)'
                      }}
                      value={set.resultValue}
                      onChange={(e) => updateSet(set.id, (s) => ({ ...s, resultValue: e.target.value }))}
                      placeholder={set.resultType === 'date' ? CONDITION_MAPPING_DATE_PLACEHOLDER : '設定値'}
                    />
                  </div>
                )}
              </div>
              {errors?.[`set-${set.id}`] && <ChrofyShowErrors field={`set-${set.id}`} errors={errors ?? {}} />}
            </div>
          )
        })}
        <div className="mb-4">
          <button
            type="button"
            className="rounded bg-[#3150A5] px-3 py-1.5 text-sm font-medium text-white transition-opacity hover:opacity-90"
            onClick={addSet}
          >
            + 条件セットを追加
          </button>
        </div>
        <div className="rounded-lg border p-4" style={{ background: '#fff8e6', borderColor: '#ffc107' }}>
          <div className="flex flex-wrap items-center gap-3">
            <span className="shrink-0 text-h7 font-medium" style={{ color: '#856404' }}>
              すべての条件セットを満たさない場合 →
            </span>
            <div className="basis-[128px] shrink min-w-[112px]">
              <ChrofyCustomSelect
                id="multi-else-result-type"
                name="multi-else-result-type"
                value={formData?.elseResultType ?? 'fixed'}
                onChange={(e) => updateForm((fd) => ({ ...fd, elseResultType: e.target.value }))}
                className="min-w-0 w-full"
                options={RESULT_TYPE_OPTIONS}
                disabled={false}
                hasNoSelect={false}
              />
            </div>
            {(formData?.elseResultType ?? 'fixed') === 'item' && (
              <>
                <div className="basis-[180px] shrink min-w-[140px]">
                  <ChrofyCustomSelect
                    id="multi-else-result-file"
                    name="multi-else-result-file"
                    isError={Boolean(errors?.else?.length)}
                    value={formData?.elseResultFileType ?? ''}
                    onChange={(e) =>
                      updateForm((fd) => ({ ...fd, elseResultFileType: e.target.value, elseResultColumn: '' }))
                    }
                    className="min-w-0 w-full"
                    options={(fileTypes || []).map((f) => ({ label: f.mappingFileType, value: f.mappingFileType }))}
                    disabled={false}
                    hasNoSelect={true}
                  />
                </div>
                <div className="basis-[180px] shrink min-w-[140px]">
                  <ChrofyCustomSelect
                    id="multi-else-result-col"
                    name="multi-else-result-col"
                    isError={Boolean(errors?.else?.length)}
                    value={formData?.elseResultColumn ?? ''}
                    onChange={(e) => updateForm((fd) => ({ ...fd, elseResultColumn: e.target.value }))}
                    className="min-w-0 w-full"
                    options={(
                      fileTypes?.find((f) => f.mappingFileType === formData?.elseResultFileType)?.headers ?? []
                    ).map((h) => ({ label: h, value: h }))}
                    disabled={!formData?.elseResultFileType}
                    hasNoSelect={true}
                  />
                </div>
              </>
            )}
            {(formData?.elseResultType ?? 'fixed') !== 'item' && (formData?.elseResultType ?? 'fixed') !== 'empty' && (
              <div className="basis-[260px] shrink min-w-[180px]">
                <input
                  type="text"
                  className="text-h7 h-11 w-full rounded-xl border bg-white pl-4 pr-3 outline-none transition-colors focus:border-[#297ad8]"
                  style={{
                    borderColor: errors?.else?.length ? '#f4392c' : 'rgba(116, 118, 166, 0.3)'
                  }}
                  value={formData?.elseResultValue ?? ''}
                  onChange={(e) => updateForm((fd) => ({ ...fd, elseResultValue: e.target.value }))}
                  placeholder={
                    (formData?.elseResultType ?? 'fixed') === 'date'
                      ? CONDITION_MAPPING_DATE_PLACEHOLDER
                      : 'デフォルト値'
                  }
                />
              </div>
            )}
          </div>
        </div>
        {errors?.general && <ChrofyShowErrors field="general" errors={errors ?? {}} />}
      </div>
    </div>
  )
}
